import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, doc, getDoc, addDoc, Timestamp, query, where, onSnapshot } from 'firebase/firestore';
import {
  IoArrowBack,
  IoInformationCircleOutline,
  IoCubeOutline,
  // eslint-disable-next-line no-unused-vars
  IoSave,
  IoQrCodeOutline
} from 'react-icons/io5';
import { useAuth } from '../context/AuthContext';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import SafeAreaInsets from '../components/Shared/SafeAreaInsets';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import './NuevoEnvioScreen.css'; // --- Premium CSS ---

const PROVEEDORES_ENVIO = [
  "Amazon", "MercadoLibre", "Uber", "Rappi", "Didi",
  "DHL", "Estafeta", "RedPack", "FedEx", "UPS", "iMile",
  "Otro, especifique"
];

function NuevoEnvioScreen() {
  const navigate = useNavigate();
  const { currentUser, isMoroso, userData } = useAuth();

  const [empresa, setEmpresa] = useState('');
  const [otroProveedor, setOtroProveedor] = useState('');
  const [vigencia, setVigencia] = useState('7'); // Default semanal
  const [activo, setActivo] = useState(true);
  const [esIlimitado, setEsIlimitado] = useState(true);
  const [usosMaximos, setUsosMaximos] = useState(1);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');

  // --- Nuevo: Lista de proveedores activos ---
  const [proveedoresActivos, setProveedoresActivos] = useState([]);

  useEffect(() => {
    if (!currentUser) return;

    // Consultar envios activos del usuario
    const q = query(
      collection(db, "accesos"),
      where("residenteId", "==", currentUser.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const activos = snapshot.docs
        .map(doc => doc.data())
        .filter(d => (d.tipo === 'Envio' || d.tipo?.toLowerCase().includes('paqueter') || !!d.empresa) && d.activo !== false && d.estatus !== 'Completado' && !d.accesoOriginalId)
        .map(d => d.empresa);
      setProveedoresActivos(activos);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!currentUser) {
      setError("Error: No se ha podido identificar al usuario.");
      return;
    }
    if (isMoroso) {
      setError("No puedes registrar envíos mientras tengas adeudos pendientes.");
      return;
    }

    const nombreEmpresa = empresa === 'Otro, especifique' ? otroProveedor : empresa;
    if (!nombreEmpresa) {
      alert('Por favor, especifica la empresa o proveedor.');
      return;
    }

    // --- CHECK DE DUPLICADOS ---
    if (proveedoresActivos.includes(nombreEmpresa)) {
      setError(`Ya tienes un acceso activo para ${nombreEmpresa}. Puedes editarlo en la lista.`);
      return;
    }

    setEnviando(true);

    try {
      const token = Math.floor(100000 + Math.random() * 900000).toString(); // 6 dígitos numéricos
      const dias = parseInt(vigencia, 10);
      const fechaCreacion = Timestamp.now();
      const fechaExpiracion = Timestamp.fromDate(new Date(Date.now() + dias * 24 * 60 * 60 * 1000));

      const rawCasa = userData?.numeroCasa || userData?.casaNumero || userData?.residencia || userData?.depto || userData?.unidadResidente || userData?.unidad || '';
      let cleanCasa = (rawCasa && rawCasa !== 'S/N' && rawCasa !== 'N/A' && rawCasa !== 'Sin Unidad') ? String(rawCasa).replace(/^(casa|depto)\s*/i, '').trim() : '';
      let condoId = userData?.condominioId || '';
      let resNombre = userData?.nombre || '';

      if ((!cleanCasa || !condoId) && currentUser?.uid) {
        try {
          const uSnap = await getDoc(doc(db, "usuarios", currentUser.uid));
          if (uSnap.exists()) {
            const uData = uSnap.data();
            condoId = condoId || uData.condominioId || '';
            resNombre = resNombre || uData.nombre || '';
            const cand = uData.numeroCasa || uData.casaNumero || uData.residencia || uData.depto || uData.unidadResidente || uData.unidad;
            if (cand && cand !== 'S/N' && cand !== 'N/A' && cand !== 'Sin Unidad') {
              cleanCasa = String(cand).replace(/^(casa|depto)\s*/i, '').trim();
            }
          }
        } catch (eErr) {
          console.warn("Aviso al consultar usuario en Firestore:", eErr.message);
        }
      }

      const accesosRef = collection(db, "accesos");
      await addDoc(accesosRef, {
        residenteId: currentUser.uid,
        residenteNombre: resNombre,
        condominioId: condoId,
        cuentaPadreId: userData?.cuentaPadreId || null,
        token: token,
        tipo: 'Envio',
        empresa: nombreEmpresa,
        nombreVisita: `Paquetería: ${nombreEmpresa}`,
        nombre: nombreEmpresa,
        numeroCasa: cleanCasa,
        vigencia: dias,
        activo: activo,
        fechaCreacion: fechaCreacion,
        expira: fechaExpiracion,
        esIlimitado: esIlimitado,
        usosMaximos: esIlimitado ? -1 : (parseInt(usosMaximos, 10) || 1),
        usosActuales: 0,
        horarioInicio: '09:00',
        horarioFin: '20:00',
        estatus: 'Activo'
      });

      alert(`¡Acceso para ${nombreEmpresa} creado!\nToken: ${token}`);
      navigate('/accesos/envios');

    } catch (error) {
      console.error("Error al crear acceso: ", error);
      alert('Error al crear el acceso.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/accesos" />

      <main className="ks-main">
        <SafeAreaInsets top={false} bottom={true} />

        {/* Sticky Header unificado */}
        <div className="ks-header-sticky-wrapper">
          <KondoBrandHeader showBack={false} />
        </div>

        <div className="ks-scroll-area">
          <div className="new-envio-container">
            {/* Sticky Compact Header */}
            <div className="new-envio-fixed-wrapper">
              <div className="new-envio-banner-compact">
                <button type="button" onClick={() => navigate('/accesos/envios')} className="new-back-btn glass" aria-label="Volver">
                  <IoArrowBack size={24} />
                </button>
                <div className="new-envio-banner-content">
                  <h2>Autorizar Paquetería</h2>
                  <p>Genera un código QR temporal</p>
                </div>
                <div className="new-envio-banner-icon">
                  <IoQrCodeOutline />
                </div>
              </div>
            </div>

            <div className="new-envio-card">
              {/* --- Info Widget --- */}
              <div className="new-envio-info-box">
                <div className="info-icon-green"><IoInformationCircleOutline size={24} /></div>
                <div className="info-content">
                  <h4>Recepción de Paquetes</h4>
                  <p>Puedes definir una vigencia extendida si esperas múltiples entregas de un mismo proveedor (ej. Amazon).</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="new-envio-form">
                {error && <div className="error-message">{error}</div>}

                <div className="form-group-grid">
                  <div className="form-field">
                    <label>Empresa / Proveedor</label>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={empresa}
                        onChange={(e) => setEmpresa(e.target.value)}
                        className="form-select"
                        required
                      >
                        <option value="">Selecciona...</option>
                        {PROVEEDORES_ENVIO.map(p => {
                          const isTaken = proveedoresActivos.includes(p);
                          return (
                            <option key={p} value={p} disabled={isTaken} style={{ color: isTaken ? 'gray' : 'black' }}>
                              {p} {isTaken ? '(Ya activo)' : ''}
                            </option>
                          );
                        })}
                      </select>
                      <IoCubeOutline size={20} style={{ position: 'absolute', right: 12, top: 12, color: '#94a3b8', pointerEvents: 'none' }} />
                    </div>
                  </div>

                  <div className="form-field">
                    <label>Vigencia del Código</label>
                    <select
                      value={vigencia}
                      onChange={(e) => setVigencia(e.target.value)}
                      className="form-select"
                      required
                    >
                      <option value="1">1 Día</option>
                      <option value="7">7 Días (Semanal)</option>
                      <option value="30">30 Días (Mensual)</option>
                    </select>
                  </div>
                </div>

                {/* Configuración de Usos (Ilimitado vs Número) */}
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '14px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>Entregas / Usos Permitidos</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Recomendado ilimitado para no cambiar de código en cada pedido de Amazon.</div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', background: esIlimitado ? '#ecfdf5' : '#f1f5f9', border: `1px solid ${esIlimitado ? '#10b981' : '#cbd5e1'}`, padding: '6px 12px', borderRadius: '20px', fontWeight: 700, fontSize: '0.8rem', color: esIlimitado ? '#047857' : '#64748b' }}>
                      <input
                        type="checkbox"
                        checked={esIlimitado}
                        onChange={(e) => setEsIlimitado(e.target.checked)}
                        style={{ accentColor: '#10b981', width: '16px', height: '16px' }}
                      />
                      <span>{esIlimitado ? '✓ Ilimitado' : 'Limitar usos'}</span>
                    </label>
                  </div>

                  {!esIlimitado && (
                    <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <label style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 600 }}>Número de usos:</label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        value={usosMaximos}
                        onChange={(e) => setUsosMaximos(parseInt(e.target.value, 10) || 1)}
                        className="form-input"
                        style={{ width: '90px', padding: '6px 10px' }}
                      />
                    </div>
                  )}
                </div>

                {empresa === 'Otro, especifique' && (
                  <div className="form-field">
                    <label>Nombre del Proveedor</label>
                    <input
                      type="text"
                      value={otroProveedor}
                      onChange={(e) => setOtroProveedor(e.target.value)}
                      className="form-input"
                      placeholder="Ej. Paquetería Local"
                      maxLength={20}
                      required
                    />
                  </div>
                )}

                <div className="checkbox-group">
                  <div style={{ flex: 1 }}>
                    <div className="checkbox-label">Activar Acceso</div>
                    <div className="checkbox-hint">Permitir entrada inmediatamente</div>
                  </div>
                  <input
                    type="checkbox"
                    className="toggle-switch"
                    checked={activo}
                    onChange={(e) => setActivo(e.target.checked)}
                  />
                </div>

                <button type="submit" className="btn-create" disabled={enviando || isMoroso}>
                  {enviando ? 'Creando...' : 'Crear Acceso QR'}
                  {!enviando && <IoQrCodeOutline size={20} />}
                </button>
              </form>
            </div>
          </div>
        </div>

        <KondoBottomNav activeRoute="/accesos" />
      </main>
    </div>
  );
}

export default NuevoEnvioScreen;